import { useCallback, useEffect, useState } from 'react'
import { Users, X, Eye, EyeOff, Building2, Loader2, SquarePen, Trash2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../../auth/store/authStore'
import { peopleService } from '../../../services/peopleService'
import { propertiesService } from '../../../services/propertiesService'
import type { User, Property } from '../../../types/api.types'
import { EmptyState } from './EmptyState'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { useSelectedPropertyId, useSelectedPropertyName } from '../../../store/propertyStore'

const ROLE_LABELS: Record<string, string> = {
  STAFF: 'Staff',
  MANAGER: 'Branch Manager',
  SUPER_ADMIN: 'Super Admin',
}

interface UserFormState {
  name: string
  email: string
  phone: string
  role: string
  password: string
  propertyIds: number[]
}

const EMPTY_FORM: UserFormState = {
  name: '',
  email: '',
  phone: '',
  role: 'STAFF',
  password: '',
  propertyIds: [],
}

interface FormErrors {
  name?: string
  email?: string
  phone?: string
  password?: string
  propertyIds?: string
}

export function UsersTab(): JSX.Element {
  const authUser = useAuthStore((s) => s.user)

  const [users, setUsers] = useState<User[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const selectedPropertyId = useSelectedPropertyId()
  const selectedPropertyName = useSelectedPropertyName()

  const [showModal, setShowModal] = useState(false)
  const [editingUserId, setEditingUserId] = useState<number | null>(null)
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const [allUsers, allProps] = await Promise.all([
        peopleService.getAll(),
        propertiesService.getAll(),
      ])
      setUsers(allUsers)
      setProperties(allProps)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const filteredUsers = selectedPropertyId
    ? users.filter(u => u.property_ids?.includes(selectedPropertyId))
    : users

  function resetForm() {
    setForm(EMPTY_FORM)
    setFormErrors({})
    setEditingUserId(null)
    setShowPassword(false)
  }

  function openCreateModal() {
    resetForm()
    setShowModal(true)
  }

  async function openEditModal(userId: number) {
    const user = await peopleService.getById(String(userId))
    if (!user) { toast.error('Could not load user details'); return }
    setForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'STAFF',
      password: '',
      propertyIds: user.property_ids || [],
    })
    setFormErrors({})
    setEditingUserId(userId)
    setShowPassword(false)
    setShowModal(true)
  }

  function validateForm(): boolean {
    const errors: FormErrors = {}
    if (!form.name.trim() || form.name.trim().length > 255) errors.name = 'Name is required (1-255 characters)'
    if (form.email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      errors.email = 'Please enter a valid email address'
    }
    if (form.phone) {
      const digits = form.phone.replace(/[\s\-()+]/g, '')
      if (digits.length < 7 || digits.length > 15) errors.phone = 'Phone number must be 7-15 digits'
    }
    if (!editingUserId && !form.password) {
      errors.password = 'Password is required'
    }
    if (form.password && (form.password.length < 8 || !/[a-zA-Z]/.test(form.password) || !/[0-9]/.test(form.password))) {
      errors.password = 'Password must be at least 8 characters with letters and numbers'
    }
    if (form.propertyIds.length === 0) errors.propertyIds = 'Select at least one property'
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit() {
    if (!validateForm()) return
    setSubmitting(true)
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        role: form.role,
        property_ids: form.propertyIds,
        is_active: true,
      }
      if (!editingUserId) {
        payload.organization_id = authUser?.organization_id
        payload.password = form.password
        await peopleService.create(payload as Partial<User> & { password?: string })
        toast.success('User created successfully')
      } else {
        if (form.password) payload.password = form.password
        await peopleService.update(String(editingUserId), payload as Partial<User> & { password?: string })
        toast.success('User updated successfully')
      }
      setShowModal(false)
      resetForm()
      await loadData()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save user'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(userId: number) {
    if (!window.confirm('Are you sure? This action cannot be undone.')) return
    try {
      await peopleService.delete(String(userId))
      toast.success('User deleted successfully')
      await loadData()
    } catch {
      toast.error('Failed to delete user')
    }
  }

  function toggleProperty(propId: number) {
    setForm(prev => ({
      ...prev,
      propertyIds: prev.propertyIds.includes(propId)
        ? prev.propertyIds.filter(id => id !== propId)
        : [...prev.propertyIds, propId],
    }))
    if (formErrors.propertyIds) setFormErrors(prev => ({ ...prev, propertyIds: undefined }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading users...</span>
      </div>
    )
  }

  if (loadError) {
    return <LoadErrorState message="Couldn't load users" onRetry={loadData} />
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-semibold text-gray-700">Viewing: {selectedPropertyName ?? 'All Properties'}</p>

        <button
          type="button"
          onClick={openCreateModal}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-70"
        >
          Create User
        </button>
      </div>

      {filteredUsers.length === 0 ? (
        <EmptyState icon={Users} message="No users found" hint={selectedPropertyId ? 'No users are assigned to the selected property' : undefined} />
      ) : (
        <>
        {/* Mobile card list */}
        <div className="space-y-3 md:hidden">
          {filteredUsers.map(u => (
            <div key={u.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-gray-900">{u.name}</p>
                  <p className="truncate text-[13px] text-brand-green">{u.email || '—'}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button type="button" onClick={() => openEditModal(u.id)} aria-label={`Edit ${u.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-brand-green hover:bg-brand-green/10">
                    <SquarePen size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(u.id)} aria-label={`Delete ${u.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-red-400 hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-gray-700">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[12px] font-medium">{ROLE_LABELS[u.role] ?? u.role}</span>
                <span>{u.phone || '—'}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-x-auto rounded-lg border border-gray-200 md:block">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Name</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Email</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Role</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Phone Number</th>
                <th className="px-6 py-3 text-center text-[13px] font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-6 py-4 text-center text-[14px] text-gray-900">{u.name}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-brand-green">{u.email || '—'}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{ROLE_LABELS[u.role] ?? u.role}</td>
                  <td className="px-6 py-4 text-center text-[14px] text-gray-700">{u.phone || '—'}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <button type="button" onClick={() => openEditModal(u.id)} className="text-brand-green hover:text-brand-green/70">
                        <SquarePen size={16} />
                      </button>
                      <button type="button" onClick={() => handleDelete(u.id)} className="text-red-400 hover:text-red-600">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingUserId ? 'Edit User Information' : 'Create User'}
              </h2>
              <button type="button" onClick={() => { setShowModal(false); resetForm() }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Name*</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => { setForm({ ...form, name: e.target.value }); if (formErrors.name) setFormErrors(prev => ({ ...prev, name: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green ${formErrors.name ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="Enter your full name."
                />
                {formErrors.name && <p className="mt-1 text-[12px] text-red-500">{formErrors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Email*</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => { setForm({ ...form, email: e.target.value }); if (formErrors.email) setFormErrors(prev => ({ ...prev, email: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green ${formErrors.email ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="username@gmail.com"
                />
                {formErrors.email && <p className="mt-1 text-[12px] text-red-500">{formErrors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Phone Number*</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => { setForm({ ...form, phone: e.target.value }); if (formErrors.phone) setFormErrors(prev => ({ ...prev, phone: undefined })) }}
                  className={`w-full rounded-lg border px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green ${formErrors.phone ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="+923001234567"
                />
                {formErrors.phone && <p className="mt-1 text-[12px] text-red-500">{formErrors.phone}</p>}
              </div>

              {/* Role */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Role*</label>
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                >
                  <option value="STAFF">Staff</option>
                  <option value="MANAGER">Branch Manager</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </div>

              {/* Password */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Password*</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => { setForm({ ...form, password: e.target.value }); if (formErrors.password) setFormErrors(prev => ({ ...prev, password: undefined })) }}
                    className={`w-full rounded-lg border py-2.5 pl-3 pr-10 text-[14px] text-gray-900 outline-none focus:border-brand-green ${formErrors.password ? 'border-red-400' : 'border-gray-200'}`}
                    placeholder={editingUserId ? 'Leave blank to keep current' : '••••••••'}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {formErrors.password && <p className="mt-1 text-[12px] text-red-500">{formErrors.password}</p>}
              </div>

              {/* Properties multi-select */}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Property*</label>
                <div className={`max-h-40 overflow-y-auto rounded-lg border p-2 ${formErrors.propertyIds ? 'border-red-400' : 'border-gray-200'}`}>
                  {properties.map(p => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={form.propertyIds.includes(p.id)}
                        onChange={() => toggleProperty(p.id)}
                        className="accent-brand-green"
                      />
                      <Building2 size={14} className="text-gray-400" />
                      {p.name}
                    </label>
                  ))}
                </div>
                {formErrors.propertyIds && <p className="mt-1 text-[12px] text-red-500">{formErrors.propertyIds}</p>}
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : editingUserId ? 'Edit' : 'Create user'}
              </button>
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm() }}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
